import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Search, 
  Plus, 
  Filter, 
  Calendar,
  Heart,
  Bookmark,
  Clock,
  CheckCircle,
  ThumbsUp,
  MapPin,
  Users,
  DollarSign
} from 'lucide-react';

/**
 * Empty States Component
 * Handles various empty state scenarios for the My Camps dashboard
 */
const EmptyStates = ({ 
  type = 'no-camps', 
  activeTab = 'all',
  searchQuery = '',
  yearFilter = '',
  onAddCamp,
  onDiscover,
  onClearFilters,
  onClearSearch 
}) => {
  // No camps in category
  const NoCampsInCategory = () => {
    const getCategoryInfo = () => {
      const categoryMap = {
        'all': { icon: Calendar, title: 'No camps yet', message: 'Start building your summer camp collection!' },
        'favorite': { icon: Heart, title: 'No favorites yet', message: 'Mark camps as favorites to see them here.' },
        'booked': { icon: CheckCircle, title: 'No booked camps', message: 'Book camps to track your confirmed registrations.' },
        'considering': { icon: Clock, title: 'No camps under consideration', message: 'Add camps you\'re thinking about to this list.' },
        'attended': { icon: Calendar, title: 'No attended camps', message: 'Mark camps as attended to track your history.' },
        'saved': { icon: Bookmark, title: 'No saved camps', message: 'Save camps for later to revisit them.' },
        'recommended': { icon: ThumbsUp, title: 'No recommended camps', message: 'Camps recommended for you will appear here.' }
      };
      
      return categoryMap[activeTab] || categoryMap['all'];
    };

    const categoryInfo = getCategoryInfo();
    const IconComponent = categoryInfo.icon;
    const isAllTab = activeTab === 'all';

    // Enhanced empty state for "all" tab (first-time users)
    // Show enhanced design for first-time users, handle onDiscover gracefully
    if (isAllTab) {
      return (
        <div className="relative w-full px-6 py-16">
          {/* Gradient Background Card */}
          <div className="relative rounded-2xl bg-gradient-to-br from-purple-50 via-white to-blue-50 border border-purple-100 shadow-lg overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-200 rounded-full opacity-10 blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-200 rounded-full opacity-10 blur-3xl translate-y-1/2 -translate-x-1/2" />
            
            {/* Content */}
            <div className="relative z-10 px-8 py-12 text-center">
              {/* Heading */}
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Your Summer Adventure Awaits! ✨
              </h2>

              {/* Description */}
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
                Discover the perfect camps for your child or add ones you've already found. 
                Let's make this summer unforgettable!
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                {/* Primary CTA - Discover */}
                <Button
                  onClick={onDiscover || onAddCamp}
                  className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Search className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                    Discover Camps
                  </span>
                </Button>

                {/* Secondary CTA - Add Manually */}
                <Button
                  onClick={onAddCamp}
                  variant="outline"
                  className="w-full sm:w-auto px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Plus className="w-5 h-5" />
                    Add Camp Manually
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Standard empty state for other tabs
    return (
      <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
            <IconComponent className="h-8 w-8 text-gray-400" />
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {categoryInfo.title}
          </h3>
          
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {categoryInfo.message}
          </p>
          
          <Button
            onClick={onAddCamp}
            className="bg-brand-600 hover:bg-brand-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Camp
          </Button>
        </CardContent>
      </Card>
    );
  };

  // No search results
  const NoSearchResults = () => (
    <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Search className="h-8 w-8 text-gray-400" />
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No camps match your search
        </h3>
        
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Try different keywords or check your filters to find what you're looking for.
        </p>
        
        <div className="flex gap-3 justify-center flex-wrap">
          <Button
            variant="outline"
            onClick={onClearSearch}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Clear Search
          </Button>
          <Button
            variant="outline"
            onClick={onClearFilters}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Clear Filters
          </Button>
          {onAddCamp && (
            <Button
              onClick={onAddCamp}
              className="bg-brand-600 text-white hover:bg-brand-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Camp
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Calendar view placeholder
  const CalendarViewPlaceholder = () => (
    <Card className="border-dashed border-2 border-gray-200 bg-gray-50">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Calendar className="h-8 w-8 text-gray-400" />
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Timeline view coming soon!
        </h3>
        
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          We're working on a beautiful timeline view to help you visualize your camp schedule.
        </p>
        
        <Button
          variant="outline"
          onClick={() => {/* Switch to grid view */}}
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Switch to Grid View
        </Button>
      </CardContent>
    </Card>
  );

  // No camps for selected year
  const NoCampsForYear = () => (
    <Card className="text-center py-12">
      <CardContent className="space-y-4">
        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
          <Calendar className="w-8 h-8 text-gray-400" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            No camps for {yearFilter}
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            You don't have any camps scheduled for {yearFilter} yet. Try selecting a different year or add some camps for this year.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={onClearFilters}
            className="px-6"
          >
            Clear Year Filter
          </Button>
          <Button
            onClick={onAddCamp}
            className="px-6 bg-brand-600 hover:bg-brand-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Camp
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render appropriate empty state
  switch (type) {
    case 'no-search-results':
      return <NoSearchResults />;
    case 'no-year-results':
      return <NoCampsForYear />;
    case 'calendar-view':
      return <CalendarViewPlaceholder />;
    case 'no-camps':
    default:
      return <NoCampsInCategory />;
  }
};

/**
 * Loading States Component
 * Handles various loading scenarios
 */
const LoadingStates = ({ 
  type = 'camps-grid',
  count = 6 
}) => {
  // Grid loading skeleton
  const GridLoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} className="animate-pulse">
          <div className="h-40 bg-gray-200"></div>
          <CardContent className="p-4 space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // List loading skeleton
  const ListLoadingSkeleton = () => (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} className="animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gray-200 rounded-lg"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
              <div className="w-16 h-8 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Status cards loading skeleton
  const StatusCardsLoadingSkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} className="animate-pulse">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 bg-gray-200 rounded-full mx-auto mb-2"></div>
            <div className="h-6 bg-gray-200 rounded mb-1"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Filters loading skeleton
  const FiltersLoadingSkeleton = () => (
    <div className="space-y-4">
      <div className="hidden lg:flex lg:items-center lg:gap-3 lg:flex-wrap">
        <div className="h-10 bg-gray-200 rounded w-64 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-44 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
      </div>
      
      <div className="hidden md:flex md:flex-col lg:hidden gap-3">
        <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center gap-3">
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-40 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-44 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>
      </div>
      
      <div className="md:hidden space-y-3">
        <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center justify-between">
          <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>
      </div>
    </div>
  );

  // Render appropriate loading state
  switch (type) {
    case 'camps-list':
      return <ListLoadingSkeleton />;
    case 'status-cards':
      return <StatusCardsLoadingSkeleton />;
    case 'filters':
      return <FiltersLoadingSkeleton />;
    case 'camps-grid':
    default:
      return <GridLoadingSkeleton />;
  }
};

/**
 * Error States Component
 * Handles various error scenarios
 */
const ErrorStates = ({ 
  type = 'load-error',
  onRetry,
  error 
}) => {
  // Load error
  const LoadError = () => (
    <Card className="border-red-200 bg-red-50">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <div className="text-red-500 text-2xl">⚠️</div>
        </div>
        
        <h3 className="text-lg font-semibold text-red-900 mb-2">
          Failed to load camps
        </h3>
        
        <p className="text-red-700 mb-6 max-w-md mx-auto">
          {error?.message || 'Something went wrong while loading your camps. Please try again.'}
        </p>
        
        <Button
          onClick={onRetry}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          Try Again
        </Button>
      </CardContent>
    </Card>
  );

  // Network error
  const NetworkError = () => (
    <Card className="border-orange-200 bg-orange-50">
      <CardContent className="p-8 text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <div className="text-orange-500 text-2xl">📡</div>
        </div>
        
        <h3 className="text-lg font-semibold text-orange-900 mb-2">
          Connection issue
        </h3>
        
        <p className="text-orange-700 mb-6 max-w-md mx-auto">
          Check your internet connection and try again.
        </p>
        
        <Button
          onClick={onRetry}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          Retry
        </Button>
      </CardContent>
    </Card>
  );

  // Render appropriate error state
  switch (type) {
    case 'network-error':
      return <NetworkError />;
    case 'load-error':
    default:
      return <LoadError />;
  }
};

export { EmptyStates, LoadingStates, ErrorStates };
